import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { Landmark, Undo2, Check, X as XIcon, Wallet } from 'lucide-react';
import api, { errorMessage, toPage } from '../services/api';
import ConfirmationPopup from '../components/ConfirmationPopup';
import {
  PageHeader,
  Card,
  Segmented,
  SearchInput,
  Pagination,
  Sheet,
  Button,
  CopyButton,
  InfoRow,
  Avatar,
  WithdrawalStatusBadge,
  ErrorBanner,
  SkeletonList,
  EmptyState,
  ResponsiveList,
  inputClass,
  useDebounce,
  cx
} from '../components/ui';
import { formatCurrency, formatDateTime, formatRelative, daysSince } from '../utils/format';

const REJECT_REASONS = [
  'Sai thông tin thanh toán',
  'Tên chủ tài khoản không khớp',
  'Nghi ngờ gian lận',
  'Số dư không hợp lệ'
];

const buildVietQRUrl = (row) => {
  const details = row?.payment_details || {};
  const accountNumber = details.account_number;
  const amountNumber = Math.round(Number(row?.amount || 0));
  const bankName = details.bank_name;
  if (!accountNumber || !amountNumber || !bankName) return '';
  const bankCode = bankName.replace(/\s+/g, '');
  const params = new URLSearchParams({ amount: String(amountNumber), addInfo: transferNote(row) });
  return `https://img.vietqr.io/image/${bankCode}-${accountNumber}-compact2.png?${params.toString()}`;
};

const transferNote = (row) => `Rut tien CaskBack #${row.id}${row?.user?.email ? ' - ' + row.user.email : ''}`;

const accountName = (row) => row?.payment_details?.account_name || row?.payment_details?.account_holder || '—';

const WithdrawalsPage = () => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('PENDING');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [selected, setSelected] = useState(null);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [confirm, setConfirm] = useState(null); // { type: 'approve' | 'revert', row }
  const [busy, setBusy] = useState(false);
  const debouncedSearch = useDebounce(search);

  const queryKey = ['withdrawals', status, debouncedSearch, page, limit];
  const { data, isLoading, isFetching, error, refetch } = useQuery(
    queryKey,
    async () => {
      const params = { page, limit };
      if (status) params.status = status;
      if (debouncedSearch) params.search = debouncedSearch;
      return toPage(await api.get('/admin/withdrawals', { params }));
    },
    { keepPreviousData: true }
  );

  const rows = data?.items || [];
  const summary = data?.summary || {};
  const pagination = data?.pagination || {};

  const refreshAll = () => {
    queryClient.invalidateQueries('withdrawals');
    queryClient.invalidateQueries('nav-counts');
    queryClient.invalidateQueries('dashboard');
  };

  const openRow = (row) => {
    setSelected(row);
    setRejectMode(false);
    setRejectReason('');
    setCustomReason('');
  };

  const closeSheet = () => !busy && setSelected(null);

  const runAction = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(successMsg);
      setConfirm(null);
      setSelected(null);
      refreshAll();
    } catch (e) {
      toast.error(errorMessage(e, 'Thao tác thất bại'));
    } finally {
      setBusy(false);
    }
  };

  const approve = (row) => runAction(() => api.post(`/admin/withdrawals/${row.id}/approve`), `Đã duyệt yêu cầu #${row.id}`);
  const revert = (row) => runAction(() => api.post(`/admin/withdrawals/${row.id}/revert`), `Đã hoàn tác yêu cầu #${row.id}`);
  const reject = (row) => {
    const notes = (rejectReason === 'OTHER' ? customReason : rejectReason).trim();
    if (!notes) {
      toast.error('Chọn hoặc nhập lý do từ chối');
      return;
    }
    // Backend đọc admin_notes (trước đây gửi nhầm "notes" nên lý do không được lưu/gửi cho user)
    runAction(() => api.post(`/admin/withdrawals/${row.id}/reject`, { admin_notes: notes }), `Đã từ chối yêu cầu #${row.id}`);
  };

  const changeStatus = (v) => {
    setStatus(v);
    setPage(1);
  };

  const tabs = [
    { value: 'PENDING', label: 'Chờ duyệt', count: summary.PENDING?.count },
    { value: 'COMPLETED', label: 'Đã chuyển', count: summary.COMPLETED?.count },
    { value: 'CANCELLED', label: 'Từ chối', count: summary.CANCELLED?.count },
    { value: '', label: 'Tất cả' }
  ];

  const renderCard = (row) => {
    const age = daysSince(row.created_at);
    return (
      <Card as="button" type="button" onClick={() => openRow(row)} className="w-full p-4 text-left active:bg-gray-50">
        <div className="flex items-start gap-3">
          <Avatar name={row.user?.name} email={row.user?.email} />
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-gray-900">{row.user?.name || row.user?.email || '—'}</p>
                <p className="truncate text-xs text-gray-500">{row.user?.email}</p>
              </div>
              <WithdrawalStatusBadge status={row.status} />
            </div>
            <p className="mt-2 text-lg font-semibold text-gray-900">{formatCurrency(row.amount)}</p>
            <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-gray-500">
              <Landmark className="h-3.5 w-3.5 flex-shrink-0" />
              {row.payment_details?.bank_name || '—'} · {row.payment_details?.account_number || '—'}
            </p>
            <p className={cx('mt-1 text-xs', row.status === 'PENDING' && age >= 3 ? 'font-medium text-red-600' : 'text-gray-400')}>
              #{row.id} · {formatRelative(row.created_at)}
            </p>
          </div>
        </div>
      </Card>
    );
  };

  const columns = [
    { key: 'id', title: '#', render: (r) => <span className="text-gray-500">{r.id}</span> },
    {
      key: 'user',
      title: 'Người dùng',
      render: (r) => (
        <div className="min-w-0">
          <p className="font-medium text-gray-900">{r.user?.name || '—'}</p>
          <p className="text-xs text-gray-500">{r.user?.email}</p>
        </div>
      )
    },
    { key: 'amount', title: 'Số tiền', align: 'right', render: (r) => <span className="font-semibold">{formatCurrency(r.amount)}</span> },
    {
      key: 'bank',
      title: 'Tài khoản nhận',
      render: (r) => (
        <div>
          <p>{r.payment_details?.bank_name || '—'}</p>
          <p className="font-mono text-xs text-gray-500">{r.payment_details?.account_number || '—'}</p>
        </div>
      )
    },
    { key: 'status', title: 'Trạng thái', render: (r) => <WithdrawalStatusBadge status={r.status} /> },
    {
      key: 'time',
      title: 'Thời gian',
      render: (r) => (
        <div className="text-xs">
          <p>Tạo: {formatDateTime(r.created_at)}</p>
          {r.processed_at && <p className="text-gray-500">Xử lý: {formatDateTime(r.processed_at)}</p>}
        </div>
      )
    }
  ];

  const qr = selected ? buildVietQRUrl(selected) : '';

  return (
    <div>
      <PageHeader
        title="Thanh toán rút tiền"
        subtitle={summary.PENDING?.count
          ? `${summary.PENDING.count} yêu cầu chờ · cần chi ${formatCurrency(summary.PENDING.amount)}`
          : 'Không có yêu cầu đang chờ'}
      />

      <div className="filter-bar">
        <Segmented options={tabs} value={status} onChange={changeStatus} />
        <SearchInput className="lg:min-w-[18rem] lg:flex-1" value={search} onChange={(v) => { setSearch(v); setPage(1); }} placeholder="Tìm theo email hoặc tên" />
      </div>

      <ErrorBanner message={error ? (errorMessage(error, 'Không thể tải danh sách rút tiền')) : ''} onRetry={refetch} />

      {isLoading ? (
        <SkeletonList />
      ) : (
        <ResponsiveList
          items={rows}
          renderCard={renderCard}
          columns={columns}
          onRowClick={openRow}
          empty={<EmptyState icon={Wallet} title="Không có yêu cầu nào" description={status === 'PENDING' ? 'Đã xử lý hết các yêu cầu rút tiền.' : undefined} />}
        />
      )}

      {pagination.total > 0 && (
        <Pagination
          page={page}
          totalPages={pagination.pages}
          total={pagination.total}
          onChange={setPage}
          limit={limit}
          onLimitChange={(n) => { setLimit(n); setPage(1); }}
          disabled={isFetching}
        />
      )}

      {/* Chi tiết + xử lý */}
      <Sheet
        open={!!selected}
        onClose={closeSheet}
        title={selected ? `Yêu cầu rút #${selected.id}` : ''}
        footer={selected && (
          selected.status === 'PENDING' ? (
            rejectMode ? (
              <div className="grid grid-cols-2 gap-3">
                <Button variant="outline" onClick={() => setRejectMode(false)} disabled={busy}>Quay lại</Button>
                <Button variant="danger" onClick={() => reject(selected)} loading={busy}>Xác nhận từ chối</Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <Button variant="outline" className="text-red-600" onClick={() => setRejectMode(true)} disabled={busy}>
                  <XIcon className="h-4 w-4" /> Từ chối
                </Button>
                <Button variant="success" onClick={() => setConfirm({ type: 'approve', row: selected })} disabled={busy}>
                  <Check className="h-4 w-4" /> Đã chuyển khoản
                </Button>
              </div>
            )
          ) : selected.status === 'COMPLETED' ? (
            <Button variant="outline" className="w-full" onClick={() => setConfirm({ type: 'revert', row: selected })} disabled={busy}>
              <Undo2 className="h-4 w-4" /> Hoàn tác về "Chờ duyệt"
            </Button>
          ) : null
        )}
      >
        {selected && (
          <div className="space-y-4">
            <div className="text-center">
              <p className="text-sm text-gray-500">Số tiền cần chuyển</p>
              <p className="text-3xl font-bold text-gray-900">{formatCurrency(selected.amount)}</p>
              <div className="mt-1 flex justify-center"><WithdrawalStatusBadge status={selected.status} /></div>
            </div>

            {selected.status === 'PENDING' && !rejectMode && (
              <div className="flex justify-center">
                {qr ? (
                  <img src={qr} alt="VietQR" className="w-full max-w-[260px] rounded-xl border" />
                ) : (
                  <div className="w-full rounded-xl border border-dashed p-4 text-center text-sm text-gray-500">Thiếu thông tin ngân hàng để tạo QR</div>
                )}
              </div>
            )}

            {rejectMode ? (
              <div>
                <p className="mb-2 text-sm font-medium text-gray-700">Lý do từ chối (sẽ gửi cho người dùng)</p>
                <div className="flex flex-wrap gap-2">
                  {[...REJECT_REASONS, 'OTHER'].map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setRejectReason(r)}
                      className={cx(
                        'rounded-full border px-3 py-2 text-sm',
                        rejectReason === r ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-200 text-gray-600'
                      )}
                    >
                      {r === 'OTHER' ? 'Lý do khác' : r}
                    </button>
                  ))}
                </div>
                {rejectReason === 'OTHER' && (
                  <input className={cx(inputClass, 'mt-3')} placeholder="Nhập lý do" value={customReason} onChange={(e) => setCustomReason(e.target.value)} autoFocus />
                )}
                <p className="mt-3 text-xs text-gray-500">Từ chối sẽ hoàn lại số tiền vào số dư khả dụng của người dùng.</p>
              </div>
            ) : (
              <Card className="divide-y divide-gray-100 px-4">
                <InfoRow label="Chủ tài khoản">{accountName(selected)}</InfoRow>
                <InfoRow label="Ngân hàng">{selected.payment_details?.bank_name || '—'}</InfoRow>
                <InfoRow label="Số tài khoản">
                  <span className="inline-flex items-center gap-1 font-mono">
                    {selected.payment_details?.account_number || '—'}
                    <CopyButton value={selected.payment_details?.account_number} />
                  </span>
                </InfoRow>
                <InfoRow label="Nội dung CK">
                  <span className="inline-flex items-center gap-1">
                    <span className="text-xs">{transferNote(selected)}</span>
                    <CopyButton value={transferNote(selected)} />
                  </span>
                </InfoRow>
                <InfoRow label="Người dùng">{selected.user?.email || '—'}</InfoRow>
                <InfoRow label="Tạo lúc">{formatDateTime(selected.created_at)}</InfoRow>
                {selected.processed_at && <InfoRow label="Xử lý lúc">{formatDateTime(selected.processed_at)}</InfoRow>}
                {selected.admin_notes && <InfoRow label="Ghi chú">{selected.admin_notes}</InfoRow>}
              </Card>
            )}
          </div>
        )}
      </Sheet>

      <ConfirmationPopup
        isOpen={!!confirm}
        onClose={() => !busy && setConfirm(null)}
        onConfirm={() => (confirm?.type === 'approve' ? approve(confirm.row) : revert(confirm.row))}
        title={confirm?.type === 'approve' ? 'Xác nhận đã chuyển khoản' : 'Hoàn tác yêu cầu rút tiền'}
        message={confirm?.type === 'approve'
          ? `Đánh dấu đã chuyển ${formatCurrency(confirm?.row?.amount)} cho ${confirm?.row?.user?.email || 'người dùng'}? Người dùng sẽ nhận thông báo.`
          : 'Yêu cầu sẽ quay về trạng thái "Chờ duyệt" để xử lý lại.'}
        confirmText={confirm?.type === 'approve' ? 'Đã chuyển' : 'Hoàn tác'}
        type={confirm?.type === 'approve' ? 'success' : 'warning'}
        isLoading={busy}
      />
    </div>
  );
};

export default WithdrawalsPage;
